/** @jsx jsx */

/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import type { TrackingData } from 'js/lib/retracked';

import { Button, Dialog, Radio, RadioGroup, TextField, useTheme } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/skillset-learner-page';

import { stopPropagation } from './FeedbackButton';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const useStyles = () => {
  const { spacing } = useTheme();
  return {
    confirmButton: css`
      margin-right: ${spacing(12)};
    `,
  };
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sendFeedback: (reason: string) => void;
  prompt: string;
  options: string[];
  trackingData: TrackingData;
};

// We need to force the type of the mapped Radio options, otherwise the RadioGroup complains about missing properties
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RadioOption = any;
const OTHER_REASON = 'other-reason';

const FeedbackDialog = ({ isOpen, onClose, sendFeedback, prompt, options, trackingData }: Props) => {
  const styles = useStyles();
  const [feedbackReason, setFeedbackReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      onClose={onClose}
      onKeyDown={stopPropagation}
      onKeyPress={stopPropagation}
      onClick={stopPropagation}
    >
      <Dialog.HeadingGroup>{_t('Improve your recommendations')}</Dialog.HeadingGroup>

      <Dialog.Content id="dialog-content">
        <RadioGroup value={feedbackReason} onChange={(e) => setFeedbackReason(e.target.value)} label={prompt}>
          {options.map((option) => <Radio key={option} value={option} label={option} />) as RadioOption}
          <Radio value={OTHER_REASON} label={_t('Other')} />
        </RadioGroup>
        {feedbackReason === OTHER_REASON && (
          <TextField
            label=""
            data-test="other-input-field"
            maxLength={140}
            fullWidth
            type="text"
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            placeholder={_t('Please specify')}
          />
        )}
      </Dialog.Content>

      <Dialog.Actions>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="submit_negative_feedback"
          trackingData={{
            ...trackingData,
            feedbackReason,
            otherReason,
          }}
          css={styles.confirmButton}
          size="medium"
          variant="primary"
          onClick={() => {
            sendFeedback(feedbackReason === OTHER_REASON ? `Other: ${otherReason}` : feedbackReason);
            onClose();
          }}
        >
          {_t('Submit feedback')}
        </TrackedButton>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="cancel_submit_negative_feedback"
          trackingData={trackingData}
          size="medium"
          variant="secondary"
          onClick={onClose}
        >
          {_t('Cancel')}
        </TrackedButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default FeedbackDialog;
